import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchPackageConsumptionDetails } from './newInvoiceForm.helper';
import { CHECKED_ICON, UNCHECKED_ICON } from '../common/InvoiceTableCommon';
import useCurrency from '../../../../components/CurrencyProvider';

export interface PackageHeaderSummary {
  packageAmount?: number;
  serviceComponentAmount?: number;
  pharmacyComponentAmount?: number;
  netServiceAmount?: number;
  remainingAmount?: number;
  balanceAdvanceAmount?: number;
}

export interface PackageServiceRow {
  id: number | string;
  serviceId?: number | string; // Original service_id from API
  serviceName: string;
  serviceCode?: string; // Service code from API
  serviceSourceType?: string; // Service source type from API
  procedureName: string;
  gender: string;
  discountPercent: number;
  unitRate: number;
  quantity: number;
  availed: number;
  pending: number;
  unlimitedQuantity: boolean;
  adjustableHead?: boolean;
  adjustableHeadType?: string | null;
  discount_amount?: number;
  providerId?: number | null;
  providerName?: string | null;
}

/** Minimal representation of a service from the invoice list */
export interface CurrentPackageService {
  billing_source_id?: number;
  serviceCode?: string;
}

export interface PackageServicesModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (selectedServices: PackageServiceRow[]) => void;
  packageName: string;
  headerSummary?: PackageHeaderSummary;
  rows: PackageServiceRow[];
  packageId: number | string;
  /** Services from this package currently in the invoice (used to calculate availed counts) */
  currentPackageServices?: CurrentPackageService[];
  /** Billing source IDs from current invoice services to pass to API */
  billingSourceIds?: number[];
}

/** Text constants (helps with consistency & future i18n) */
const TEXT = {
  dialogTitlePrefix: 'Select Services from Package',
  packageNameLabel: 'Package Name',
  table: {
    serviceName: 'Service Name',
    procedureName: 'Procedure Name',
    gender: 'Gender',
    discountPercent: 'Discount (%)',
    unitRate: 'Unit Rate',
    quantity: 'Quantity',
    availed: 'Availed',
    pending: 'Pending',
    discountAmount: 'Discount Amount',
    netPayable: 'Net Payable',
    unlimitedQuantity: 'Unlimited Quantity',
    adjustableHead: 'Adjustable Head',
    adjustableHeadType: 'Adjustable Head Type',
  },
  header: {
    packageAmount: 'Package Amount',
    serviceComponentAmount: 'Service Component Amount',
    pharmacyComponentAmount: 'Pharmacy Component Amount',
    netServiceAmount: 'Net Service Amount',
    remainingAmount: 'Remaining Amount',
    balanceAdvanceAmount: 'Used Amount',
  },
  loading: 'Loading services...',
  empty: 'No services available in this package.',
  actions: {
    cancel: 'Cancel',
    save: 'Save',
  },
  checkbox: {
    selectAll: 'Select all',
    selectRow: 'Select', // shortened
  },
  dialogAriaLabel: 'Package services selection dialog',
  errorFallback: 'Failed to load package consumption details',
} as const;

/** Summary config using the text constants */
const SUMMARY_CONFIG: { key: keyof PackageHeaderSummary; label: string }[] = [
  { key: 'packageAmount', label: TEXT.header.packageAmount },
  { key: 'serviceComponentAmount', label: TEXT.header.serviceComponentAmount },
  { key: 'pharmacyComponentAmount', label: TEXT.header.pharmacyComponentAmount },
  { key: 'netServiceAmount', label: TEXT.header.netServiceAmount },
  { key: 'remainingAmount', label: TEXT.header.remainingAmount },
  { key: 'balanceAdvanceAmount', label: TEXT.header.balanceAdvanceAmount },
];

/** PackageServicesModal component */
const PackageServicesModal: React.FC<PackageServicesModalProps> = ({
  open,
  onClose,
  onSave,
  packageName,
  headerSummary,
  rows,
  packageId,
  currentPackageServices = [],
  billingSourceIds = [],
}) => {
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState<PackageHeaderSummary | undefined>(
    headerSummary
  );
  const [tableRows, setTableRows] = useState<PackageServiceRow[]>(rows || []);
  /** Baseline rows from API (before any local additions) to recalculate when services are removed */
  const [baselineRows, setBaselineRows] = useState<PackageServiceRow[]>([]);
  /** Track which packageId the baseline was loaded for */
  const [loadedPackageId, setLoadedPackageId] = useState<number | string | null>(null);
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Reset selection and cached data when modal closes to ensure fresh data on reopen */
  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      // Reset loaded packageId to force refetch with latest billingSourceIds on next open
      setLoadedPackageId(null);
    }
  }, [open]);

  /** Reset baseline when packageId changes to avoid showing stale data from previous package */
  useEffect(() => {
    if (packageId && loadedPackageId && String(packageId) !== String(loadedPackageId)) {
      setBaselineRows([]);
      setTableRows([]);
      setSummary(undefined);
      setLoadedPackageId(null);
    }
  }, [packageId, loadedPackageId]);

  /** Fetch package details once per packageId; stores baseline for recalculation */
  useEffect(() => {
    // Only fetch when dialog is opened for this package and we haven't yet
    // loaded baseline for this specific packageId.
    if (!open || !packageId || (loadedPackageId && String(loadedPackageId) === String(packageId))) return;

    let isCancelled = false;

    setLoading(true);
    setError(null);

    fetchPackageConsumptionDetails(packageId, billingSourceIds.length > 0 ? billingSourceIds : undefined)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        if (isCancelled) return;

        setSummary({
          packageAmount: res.package_net_amount,
          serviceComponentAmount: res.service_amount,
          pharmacyComponentAmount: res.pharmacy_amount,
          netServiceAmount: res?.service_net_amount ?? res.net_service_amount,
          remainingAmount: res.balance_amount,
          balanceAdvanceAmount: res.used_amount,
        });

        const mappedRows: PackageServiceRow[] = (res.consumption_details || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item: any, index: number) => ({
            // Use index to ensure unique id since service_id can be duplicated
            id: `${item.service_id ?? item.package_id}_${index}`,
            serviceId: item.service_id ?? item.package_id, // Store original service_id
            serviceName: item.service_name,
            serviceCode: item.service_code,
            serviceSourceType: item.service_source_type,
            procedureName: item.procedure_name || '',
            gender: item.gender,
            discountPercent: item.discount_percent ?? 0,
            // For unlimited-quantity services, treat unit rate as 0
            // so they do not add billable charges when consumed.
            unitRate: item.unlimited_quantity ? 0 : item.unit_rate ?? 0,
            quantity: item.quantity ?? 0,
            availed: item.availed ?? 0,
            pending: item.pending ?? 0,
            unlimitedQuantity: !!item.unlimited_quantity,
            adjustableHead: !!item.adjustable_head,
            adjustableHeadType: item.adjustable_head_type ?? null,
            discount_amount: Number.isFinite(item.discount_amount)
              ? item.discount_amount
              : 0,
            providerId: item.provider_id ?? null,
            providerName: item.provider_name ?? null,
          })
        );

        // Store both baseline and initial tableRows, and track which package this is for
        setBaselineRows(mappedRows);
        setTableRows(mappedRows);
        setLoadedPackageId(packageId);
      })
      .catch((e: unknown) => {
        if (isCancelled) return;
        // Prefer API-provided detail/message over generic errors
        const axiosErr = e as {
          message?: string;
          response?: { data?: { detail?: string; message?: string } };
        };
        const apiDetail =
          axiosErr?.response?.data?.detail ||
          axiosErr?.response?.data?.message;
        const message = apiDetail || axiosErr?.message || TEXT.errorFallback;
        setError(message);
      })
      .finally(() => {
        if (!isCancelled) {
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [open, packageId, loadedPackageId, billingSourceIds]);

  /**
   * Recalculate availed/pending when currentPackageServices changes.
   * This handles the case where services are removed from the invoice.
   * For each service row, count how many times it appears in currentPackageServices
   * and add that to the baseline availed count.
   */
  useEffect(() => {
    if (!open || baselineRows.length === 0) return;

    // Count how many times each serviceId appears in currentPackageServices
    // Use string keys to avoid type mismatch issues (API may return number or string)
    const serviceCountMap = new Map<string, number>();
    currentPackageServices.forEach((svc) => {
      const serviceId = svc.billing_source_id;
      if (serviceId != null) {
        const key = String(serviceId);
        serviceCountMap.set(key, (serviceCountMap.get(key) || 0) + 1);
      }
    });

    // Recalculate tableRows based on baseline + current services count
    const recalculatedRows = baselineRows.map((baseRow) => {
      if (baseRow.unlimitedQuantity) {
        return baseRow;
      }
      const key = String(baseRow.serviceId);
      const locallyAdded = serviceCountMap.get(key) || 0;
      const newAvailed = (baseRow.availed ?? 0) + locallyAdded;
      const newPending = Math.max(0, (baseRow.quantity ?? 0) - newAvailed);
      return {
        ...baseRow,
        availed: newAvailed,
        pending: newPending,
      };
    });

    setTableRows(recalculatedRows);
  }, [open, baselineRows, currentPackageServices]);

  const effectiveHeaderSummary: PackageHeaderSummary | undefined =
    summary || headerSummary;

  const effectiveRows: PackageServiceRow[] = useMemo(
    () => (tableRows && tableRows.length > 0 ? tableRows : rows || []),
    [tableRows, rows]
  );

  const isRowExhausted = (row: PackageServiceRow) =>
    row.pending === 0 && !row.unlimitedQuantity;

  const selectableRows = useMemo(
    () => effectiveRows.filter((r) => !isRowExhausted(r)),
    [effectiveRows]
  );

  const allSelected = useMemo(
    () =>
      selectableRows.length > 0 &&
      selectableRows.every((r) => selectedIds.includes(r.id)),
    [selectableRows, selectedIds]
  );

  const someSelected = useMemo(
    () =>
      selectableRows.some((r) => selectedIds.includes(r.id)) && !allSelected,
    [selectableRows, selectedIds, allSelected]
  );

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const selectableIds = selectableRows.map((r) => r.id);
      return prev.length === selectableIds.length ? [] : selectableIds;
    });
  }, [selectableRows]);

  const toggleOne = useCallback((id: number | string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSave = useCallback(() => {
    const selectedRows = effectiveRows
      .filter((r) => selectedIds.includes(r.id))
      .map((r) => ({
        ...r,
        id: r.serviceId ?? r.id, // Return original service_id as id
      }));

    // Note: We no longer manually update tableRows here.
    // When the parent adds these services to the invoice, the currentPackageServices
    // prop will change, triggering the recalculation effect to update availed/pending.

    onSave?.(selectedRows);
    onClose();
  }, [effectiveRows, selectedIds, onSave, onClose]);

  const dialogTitleText = useMemo(
    () =>
      `${TEXT.dialogTitlePrefix} ${
        packageName ? `{${packageName}}` : ''
      }`,
    [packageName]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      aria-label={TEXT.dialogAriaLabel}
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: 2,
          maxHeight: '96vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          pb: 0.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: 16, mb: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {TEXT.dialogTitlePrefix}
              {packageName && (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#1976D2',
                    bgcolor: '#E3F2FD',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                  }}
                >
                  {packageName}
                </Typography>
              )}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: 'text.secondary',
            }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {effectiveHeaderSummary && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2.5,
              px: 3,
              py: 2,
              mb: 2,
              borderRadius: 3,
              bgcolor: '#F8FAFC',
              border: '1px solid #E5E7EB',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              transition: 'background-color 120ms ease, border-color 120ms ease',
            }}
          >
            <Box sx={{ minWidth: 140, flexShrink: 0 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13, mb: 1 }}
              >
                {TEXT.packageNameLabel}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: 14 }}
              >
                {packageName || '-'}
              </Typography>
            </Box>

            {SUMMARY_CONFIG.map(({ key, label }) => (
              <Box
                key={key}
                sx={{
                  minWidth: 120,
                  flex: 1,
                  pr: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: 13, mb: 1 }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  {effectiveHeaderSummary[key] != null
                    ? formatCurrency(Number(effectiveHeaderSummary[key]))
                    : '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(96vh - 260px)',
            boxShadow: 'none',
            border: 'none',
            borderRadius: 0,
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table
            size="small"
            sx={{ borderCollapse: 'collapse', minWidth: 1500, tableLayout: 'auto' }}
            aria-label={dialogTitleText}
          >
            <TableHead>
              <TableRow
                sx={{
                  background: '#F5F6F7',
                  '& .MuiTableCell-root': { borderBottom: 'none' },
                  whiteSpace: 'nowrap',
                }}
              >
                <TableCell
                  padding="checkbox"
                  sx={{
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                  }}
                >
                  <Tooltip title={TEXT.checkbox.selectAll} arrow>
                    <Checkbox
                      size="small"
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                      icon={UNCHECKED_ICON}
                      checkedIcon={CHECKED_ICON}
                      disableRipple
                      slotProps={{
                        input: {
                          'aria-label': TEXT.checkbox.selectAll,
                        },
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>{TEXT.table.serviceName}</TableCell>
                <TableCell>{TEXT.table.procedureName}</TableCell>
                <TableCell>{TEXT.table.gender}</TableCell>
                <TableCell>{TEXT.table.discountPercent}</TableCell>
                <TableCell>{TEXT.table.unitRate}</TableCell>
                <TableCell>{TEXT.table.quantity}</TableCell>
                <TableCell>{TEXT.table.availed}</TableCell>
                <TableCell>{TEXT.table.pending}</TableCell>
                <TableCell>{TEXT.table.discountAmount}</TableCell>
                <TableCell>{TEXT.table.netPayable}</TableCell>
                <TableCell>{TEXT.table.unlimitedQuantity}</TableCell>
                <TableCell>{TEXT.table.adjustableHead}</TableCell>
                <TableCell
                  sx={{
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 12,
                  }}
                >
                  {TEXT.table.adjustableHeadType}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3, fontSize: 13 }}
                    >
                      {TEXT.loading}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : effectiveRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography
                      variant="body2"
                      color={error ? 'error' : 'text.secondary'}
                      sx={{ py: 3, fontSize: 13 }}
                    >
                      {error || TEXT.empty}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                effectiveRows.map((row, idx) => {
                  const isSelected = selectedIds.includes(row.id);
                  const isExhausted = isRowExhausted(row);
                  return (
                    <TableRow
                      key={`${row.id}_${idx}`}
                      sx={{
                        background: '#FFFFFF',
                        height: 48,
                        whiteSpace: 'nowrap',
                        '& td': {
                          borderTop: idx === 0 ? 'none' : undefined,
                          borderBottom:
                            idx === effectiveRows.length - 1
                              ? 'none'
                              : undefined,
                        },
                      }}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{
                          borderTopLeftRadius: 12,
                          borderBottomLeftRadius: 12,
                        }}
                      >
                        <Tooltip
                          title={
                            isExhausted
                              ? 'No pending quantity left for this service in this package'
                              : ''
                          }
                          arrow
                          disableHoverListener={!isExhausted}
                        >
                          <span>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => {
                                if (isExhausted) return;
                                toggleOne(row.id);
                              }}
                              icon={UNCHECKED_ICON}
                              checkedIcon={CHECKED_ICON}
                              disableRipple
                              slotProps={{
                                input: {
                                  'aria-label': TEXT.checkbox.selectRow,
                                },
                              }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500 }}
                        >
                          {row.serviceName}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.procedureName}</TableCell>
                      <TableCell>{row.gender}</TableCell>
                      <TableCell>
                        {row.discountPercent.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {row.unlimitedQuantity
                          ? '0.00 (Unlimited)'
                          : row.unitRate.toFixed(2)}
                      </TableCell>
                      <TableCell>{row.quantity}</TableCell>
                      <TableCell>{row.availed}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: 13,
                            color:
                              row.pending === 0 && !row.unlimitedQuantity
                                ? 'error.main'
                                : 'inherit',
                          }}
                        >
                          {row.pending}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.unlimitedQuantity
                          ? '0.00'
                          : formatCurrency(row.discount_amount ?? 0)}
                      </TableCell>
                      <TableCell>
                        {row.unlimitedQuantity
                          ? '0.00'
                          : formatCurrency(row.unitRate - (row.discount_amount ?? 0))}
                      </TableCell>
                      <TableCell>
                        {row.unlimitedQuantity ? 'Unlimited' : 'No'}
                      </TableCell>
                      <TableCell>
                        {row.adjustableHead ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderTopRightRadius: 12,
                          borderBottomRightRadius: 12,
                        }}
                      >
                        {row.adjustableHeadType || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          borderTop: '1px solid #E4E7EC',
          justifyContent: 'flex-end',
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{
            minWidth: 110,
            textTransform: 'none',
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          {TEXT.actions.cancel}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={selectedIds.length === 0}
          sx={{
            minWidth: 110,
            textTransform: 'none',
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          {TEXT.actions.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PackageServicesModal;